import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';
import { Languages } from '@coretava/i18n';

/* Boots i18next directly (without going through @gamiphy/react-i18n) so apps/web type-checks
 * cleanly under bundler module resolution. Mirrors the dashboard pattern (same locale files,
 * same flat keys with `keySeparator: false`).
 *
 * Locale files live in packages/i18n/locales/; Vite's import.meta.glob loads them at build time. */

const STORAGE_KEY = 'corepilot.language';

export type SupportedLanguage = Languages.en | Languages.es | Languages.pt;

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [Languages.en, Languages.es, Languages.pt];

const localeModules = import.meta.glob('../../../../packages/i18n/locales/**/*.json') as Record<
  string,
  () => Promise<{ default: Record<string, string> }>
>;

function readStoredLanguage(): SupportedLanguage | undefined {
  if (typeof window === 'undefined') return undefined;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage) ? (stored as SupportedLanguage) : undefined;
}

const PROVIDED_LANGUAGE = 'providedLanguage';

let initialized = false;

export async function initI18n(): Promise<void> {
  const detector = new LanguageDetector();
  detector.addDetector({
    name: PROVIDED_LANGUAGE,
    lookup() {
      return readStoredLanguage();
    },
  });

  if (initialized) {
    await i18next.changeLanguage(readStoredLanguage());
    return;
  }

  await i18next
    .use(detector)
    .use(
      resourcesToBackend(
        (language: string, namespace: string, callback: (err: Error | null, resources: Record<string, string> | null) => void) => {
          const key = `../../../../packages/i18n/locales/${language}/${namespace}.json`;
          const loader = localeModules[key];
          if (!loader) {
            callback(new Error(`Missing locale: ${language}/${namespace}`), null);
            return;
          }
          loader()
            .then((mod) => callback(null, mod.default))
            .catch((err: Error) => callback(err, null));
        },
      ),
    )
    .use(initReactI18next)
    .init({
      fallbackLng: Languages.en,
      supportedLngs: SUPPORTED_LANGUAGES,
      detection: { order: [PROVIDED_LANGUAGE, 'navigator', 'htmlTag'] },
      keySeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
    });

  initialized = true;
}

export async function setLanguage(language: SupportedLanguage): Promise<void> {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, language);
  }
  await i18next.changeLanguage(language);
}

export function getLanguage(): SupportedLanguage {
  const current = i18next.language as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(current) ? current : Languages.en;
}
